<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	// Design system components
	import { Button, CircleAvatar, Loader, Text, Toggle } from '@Shelly/shelly-design-system/index';
	// Helpers
	import EmptyList from 'src/components/Lists/EmptyList.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getSessionEvents,
		parseSessionEvents,
		type SessionMode,
	} from 'src/helpers/xt1_helpers/ev_charger/ev_charger_event_log_helpers';
	import EvChargeEventLogTable from './EvChargeEventLogTable.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SessionEvents } from 'src/helpers/xt1_helpers/ev_charger/ev_charger_session_info_helpers';

	export let device: Device;

	let sessionEvents: {
		plug: SessionEvents[] | undefined;
		charging: SessionEvents[] | undefined;
	} = { plug: [], charging: [] };
	let showRetry = false;
	let waitForResponse = false;
	let sessionEventsMode: SessionMode = 'plug';

	const getEventLogStatistic = async () => {
		waitForResponse = true;
		showRetry = false;
		const eventsLogData = await getSessionEvents(device);
		if (eventsLogData === null) {
			showRetry = true;
		} else {
			sessionEvents.plug = parseSessionEvents(eventsLogData, 'plug');
			sessionEvents.charging = parseSessionEvents(eventsLogData, 'charging');
			if (import.meta.env.DEV) {
				console.log('EV Charger Event Log: ', eventsLogData);
			}
		}
		waitForResponse = false;
	};

	onMount(async () => {
		await getEventLogStatistic();
	});
</script>

{#if waitForResponse}
	<div class="flex-full-height align-items-center justify-content-center">
		<Loader class="d-flex justify-content-center" />
	</div>
{:else if showRetry}
	<div class="flex-full-height justify-content-center align-items-center">
		<EmptyList
			title={$format('err_msg_fetch_activity_log', {
				default: 'There was an error fetching the activity log.',
			})}
			description={$format('err_msg_please_try_again', {
				default: 'Please try again!',
			})}
		/>
		<div class="text-center">
			<Button size="l" class="mt-m mb-m" on:click={() => getEventLogStatistic()}>
				{$format('button_retry', { default: 'Retry' })}
			</Button>
		</div>
	</div>
{:else if sessionEvents.plug.length === 0 && sessionEvents.charging.length === 0}
	<div class="flex-full-height align-items-center justify-content-center">
		<CircleAvatar class="mb-l" size={'xxl'} icon={'fa-thin fa-face-sleeping'} />
		<Text element="h2" class="mb-s text-bold text-center">
			{capFirstChar(
				$format('label_title_no_events', {
					default: 'No activity info found.',
				}),
			)}.
		</Text>
		<Text class="text-color-muted text-center" styleType="body-small">
			{$format('label_description_no_events_device', {
				default: 'Make sure the device is powered and online to report activity.',
			})}
		</Text>
	</div>
{:else}
	<Toggle
		class="mt-s"
		checked={sessionEventsMode === 'charging'}
		size="m"
		on:click={() => {
			sessionEventsMode = sessionEventsMode === 'plug' ? 'charging' : 'plug';
		}}
		id="toggle-0vtb18j"
	>
		{$format('event_log_session_mode', {
			default: 'Charging-only mode',
		})}
	</Toggle>
	{#key sessionEventsMode}
		<EvChargeEventLogTable {device} events={sessionEvents[sessionEventsMode]} />
	{/key}
{/if}
